import React, { FC } from 'react';
import { PassportsTransactionData } from '@/types';
import { timeConverter } from '@/utils/helpers';
import { Map } from '@/ui/components/Map';
import { Icon, faXmark } from '@/ui/components/Icon';

interface LocationEvidenceProps {
  transaction: PassportsTransactionData;
  onClose: () => void;
}

export const LocationEvidence: FC<LocationEvidenceProps> = ({
  transaction: t,
  onClose,
}) => {
  const position = { lat: t.latitude!, lng: t.longitude! };
  const accuracy = t.accuracy && t.accuracy > 0 ? t.accuracy : undefined;
  // "Vehicle Located. Location Changed to <to> from <from>" → show from → to.
  const move =
    t.transType === 'Vin Locate'
      ? /Location Changed to (.+?) from (.+?)\.?\s*$/i.exec(t.transDescription)
      : null;

  return (
    <section className="pb-evidence" aria-labelledby="pb-evidence-title">
      <header className="pb-evidence-bar">
        <h2 id="pb-evidence-title">Location evidence</h2>
        <button
          type="button"
          aria-label="Close location evidence"
          onClick={onClose}
        >
          <Icon icon={faXmark} />
        </button>
      </header>
      <div className="pb-evidence-body">
        <h3>
          {move ? 'Vehicle Located - Location Changed' : t.transDescription}
        </h3>
        <p className="pb-sub">
          {[
            timeConverter(t.transDate, 'DD MMM YYYY, HH:mm:ss'),
            t.transType,
            t.transVinEndStatus && `Status ${t.transVinEndStatus}`,
          ]
            .filter(Boolean)
            .join(' • ')}
        </p>
        {(t.locationDesc || move) && (
          <div className="pb-evidence-site d-flex flex-wrap gap-5">
            {t.locationDesc && <span>{t.locationDesc}</span>}
            {move && (
              <span className="pb-evidence-move">
                {move[2]} → {move[1]}
              </span>
            )}
          </div>
        )}
        <div className="pb-evidence-map">
          <span className="pb-evidence-chip">Event position</span>
          <Map
            center={position}
            markers={[position]}
            circle={{ center: position, radius: t.accuracy || 0 }}
            zoom={14}
            showSiteOverlays
          />
        </div>
        <div className="pb-evidence-gps">
          <span className="pb-label">Recorded GPS</span>
          <div className="d-flex justify-content-between">
            <strong className="pb-mono">
              {position.lat}, {position.lng}
            </strong>
            <span>
              {accuracy ? `Accuracy ${accuracy} m` : 'Accuracy not recorded'}
            </span>
          </div>
          <small>
            {t.altitude != null && `Altitude ${t.altitude} m • `}Location
            captured with this transaction
          </small>
        </div>
        <p className="pb-evidence-note">
          Recorded event location, not the vehicle’s current position.
        </p>
        <button type="button" className="pb-link" onClick={onClose}>
          View full transaction record →
        </button>
      </div>
    </section>
  );
};
